/**
 * Tells apart "this session is genuinely dead" from "we couldn't reach the
 * server right now".
 *
 * This matters because a forced logout clears the stored refresh token, which
 * is unrecoverable without someone typing a password. On an always-on store
 * device with flaky wifi, treating a network blip as an auth failure means the
 * kiosk silently signs itself out and stops working until staff intervene.
 */

/** Refresh tokens that the server has actually rejected. Nothing else. */
const DEFINITIVE_PATTERNS = [
  'refresh_token_not_found',
  'invalid refresh token',
  'refresh token not found',
  'invalid_grant',
  'token has been revoked',
  'user not found',
  'user_banned',
];

/** Transient conditions — offline, DNS, timeouts, proxies, 5xx. */
const TRANSIENT_PATTERNS = [
  'failed to fetch',
  'networkerror',
  'network request failed',
  'load failed',
  'timeout',
  'timed out',
  'aborted',
  'econnreset',
  'enotfound',
  'socket',
  'gateway',
  'service unavailable',
  'too many requests',
];

function messageOf(error: unknown): string {
  if (!error) return '';
  if (typeof error === 'string') return error.toLowerCase();
  if (typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message ?? '').toLowerCase();
  }
  return String(error).toLowerCase();
}

function statusOf(error: unknown): number | null {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = Number((error as { status?: unknown }).status);
    return Number.isFinite(status) ? status : null;
  }
  return null;
}

/**
 * True only when the credentials themselves are invalid and no amount of
 * retrying will help. Anything ambiguous returns false, so the caller retries
 * instead of destroying the session.
 */
export function isDefinitiveAuthFailure(error: unknown): boolean {
  const message = messageOf(error);
  if (!message) return false;

  // Network problems are never an auth verdict, whatever else they say.
  if (TRANSIENT_PATTERNS.some((p) => message.includes(p))) return false;

  // 5xx and 429 are the server struggling, not a rejected token.
  const status = statusOf(error);
  if (status !== null && (status >= 500 || status === 429 || status === 0)) return false;

  return DEFINITIVE_PATTERNS.some((p) => message.includes(p));
}
